import { useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { Breed } from 'shared/@generated/models';
import { falAngleDown } from 'shared/@generated/icons/falAngleDown';
import { falPlus } from 'shared/@generated/icons/falPlus';
import { falTimes } from 'shared/@generated/icons/falTimes';
import { Icon } from 'shared/components/icon';
import { SelectedBreeds, SelectedSingleBreed } from 'shared/components/selected-breeds';
import { Text } from 'shared/components/text';

export function BreedSelectionField({
  values,
  breeds,
  placeholder,
  accessibilityLabel,
  onOpen,
  onRemove,
  multiple = false,
  dense,
  testID,
  itemTestID,
}: {
  values: (number | string)[];
  breeds: Breed[];
  placeholder: string;
  accessibilityLabel: string;
  onOpen: () => void;
  onRemove: (value: number | string) => void;
  multiple?: boolean;
  dense?: boolean;
  testID: string;
  itemTestID?: (value: number | string) => string;
}) {
  const { t } = useTranslation('common');
  const isNative = Platform.OS !== 'web';
  const [actionHovered, setActionHovered] = useState(false);
  const selectedValue = values[0];
  const clearSelected = !multiple && selectedValue !== undefined;
  const selectedBreed =
    typeof selectedValue === 'number'
      ? breeds.find((breed) => breed.id === selectedValue)
      : undefined;
  const selectedText =
    selectedBreed?.name ??
    (typeof selectedValue === 'string'
      ? selectedValue
      : t('components:listings_filter_form.selected_breed'));

  return (
    <View
      accessible={false}
      className={clsx(
        'min-w-0 flex-row items-center overflow-hidden rounded border bg-white',
        actionHovered ? 'border-brand-brown-dark/30' : 'border-brand-brown-dark/15',
        { 'hover:border-brand-brown-dark/30': values.length === 0 },
        dense ? 'h-[38px] md:h-[42px]' : 'h-[54px]',
      )}
      style={
        isNative
          ? {
              borderColor: actionHovered ? 'rgba(48, 25, 17, 0.3)' : 'rgba(48, 25, 17, 0.15)',
              borderWidth: 1,
              borderRadius: 4,
              backgroundColor: '#ffffff',
              ...(Platform.OS === 'ios' ? { borderCurve: 'continuous' as const } : {}),
            }
          : undefined
      }
      testID={testID}
    >
      {values.length > 0 ? (
        <View className={'h-full min-w-0 flex-1'}>
          {multiple ? (
            <SelectedBreeds
              values={values}
              breeds={breeds}
              onRemove={onRemove}
              dense={dense}
              itemTestID={itemTestID}
            />
          ) : (
            <SelectedSingleBreed
              breed={selectedBreed}
              text={selectedText}
              dense={dense}
              testID={itemTestID?.(values[0]!)}
            />
          )}
        </View>
      ) : (
        <Pressable
          className={clsx('h-full min-w-0 flex-1 flex-row items-center', dense ? 'px-3' : 'px-4')}
          onPress={onOpen}
          accessibilityRole={'button'}
          accessibilityLabel={placeholder}
        >
          <Text
            className={clsx('text-brand-brown-dark/50 min-w-0 flex-1', {
              'text-sm md:text-base': dense,
              'text-lg': !dense,
            })}
            style={
              isNative ? { fontSize: dense ? 15 : 18, lineHeight: dense ? 20 : 22 } : undefined
            }
            numberOfLines={1}
            ellipsizeMode={'tail'}
          >
            {placeholder}
          </Text>
        </Pressable>
      )}

      <Pressable
        className={clsx('h-full shrink-0 items-center justify-center', {
          'border-brand-brown-dark/15 border-l': multiple || clearSelected,
          'w-10': dense,
          'w-12': !dense,
        })}
        accessibilityRole={'button'}
        accessibilityLabel={clearSelected ? `${t('delete')} ${selectedText}` : accessibilityLabel}
        onPress={(event) => {
          if (clearSelected) {
            event.stopPropagation();
            setActionHovered(false);
            onRemove(selectedValue);
          } else {
            onOpen();
          }
        }}
        onHoverIn={() => setActionHovered(true)}
        onHoverOut={() => setActionHovered(false)}
        testID={
          clearSelected ? `${itemTestID?.(selectedValue) ?? testID}-remove` : `${testID}-open`
        }
      >
        <Icon
          icon={clearSelected ? falTimes : multiple ? falPlus : falAngleDown}
          className={'text-brand-brown-dark size-4'}
        />
      </Pressable>
    </View>
  );
}
